import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { onMessageListener } from '../config/firebase';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Get all notifications
  const getNotifications = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(params);
      const res = await axios.get(`/api/notifications?${queryParams.toString()}`);
      setNotifications(res.data.data);
      setUnreadCount(res.data.unreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      await axios.put(`/api/notifications/${id}/read`);
      setNotifications(notifications.map(notif => 
        notif._id === id ? { ...notif, isRead: true } : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Don't show error if it's a 401 (user logged out)
      if (error.response?.status !== 401) {
        toast.error('Failed to mark notification as read');
      }
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put('/api/notifications/read-all');
      setNotifications(notifications.map(notif => ({ ...notif, isRead: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      // Don't show error if it's a 401 (user logged out)
      if (error.response?.status !== 401) {
        toast.error('Failed to mark all as read');
      }
    }
  };

  // Delete notification
  const deleteNotification = async (id) => {
    try {
      await axios.delete(`/api/notifications/${id}`);
      setNotifications(notifications.filter(notif => notif._id !== id));
      toast.success('Notification deleted');
    } catch (error) {
      // Don't show error if it's a 401 (user logged out)
      if (error.response?.status !== 401) {
        toast.error('Failed to delete notification');
      }
    }
  };

  // Listen for foreground Firebase messages
  useEffect(() => {
    onMessageListener()
      .then((payload) => {
        toast.success(
          `${payload.notification.title}: ${payload.notification.body}`,
          { duration: 5000 }
        );
        // Refresh notifications
        getNotifications();
      })
      .catch((err) => console.log('Failed to receive message:', err));
  }, [getNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    getNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
